import { Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Observable, combineLatest, of, Subject, throwError, catchError, shareReplay, startWith, map, switchMap, take } from 'rxjs';

import { PhoneVerificationReq } from "src/app/shared/components/phone-verification-dialog/PhoneVerificationReq.model";
import { PhoneVerificationReqRes } from "src/app/shared/components/phone-verification-dialog/PhoneVerificationReqRes.model";
import { BaseResponseDto } from "src/app/shared/models/BaseResponseDto.model";
import { BasePhoneVerificationStrategy } from "src/app/shared/models/phone-verification-strategies/base-phone-verification-strategy.model";
import { UnitBusinessApplicationReqRes } from "src/app/shared/models/unit-bank-account/UnitBusinessApplicationReqRes.model";
import { LoggerService } from 'src/app/shared/errors/logger.service';
import { DialogService } from 'src/app/services/dialog.service';
import { AlertDialogParams } from 'src/app/shared/components/alert-dialog/alert-dialog.component';
import { UtilsService } from "../utils.service";
import { UnitBankApplicationDataService } from "./unit-bank-application-data.service";
import { BankApplicationDataService } from "../gp/bank-application-data.service";

@Injectable()
export class UnitBankApplicationService extends BasePhoneVerificationStrategy {
  applicationId$: Observable<number> = this.route.params.pipe(
    map((params) => +params.id),
    shareReplay(1)
  );

  private _refreshApplication$ = new Subject<void>();

  application$: Observable<UnitBusinessApplicationReqRes> =
    combineLatest([this.applicationId$, this._refreshApplication$.pipe(startWith([null]))]).pipe(
      switchMap(([id, _]) => this.unitBankApplicationDataService.getById(id)),
      shareReplay(1)
    );

  organizationApplication$ = this.unitBankApplicationDataService.getOrganizationApplication().pipe(shareReplay(1));

  approvedOrganizationLevelApplications$ = this.unitBankApplicationDataService.getAvailableOrganizationLevelApplications().pipe(shareReplay(1));

  constructor(
    private route: ActivatedRoute,
    private unitBankApplicationDataService: UnitBankApplicationDataService,
    private bankApplicationDataService: BankApplicationDataService,
    private loggerService: LoggerService,
    private dialogService: DialogService,
    private utilsService: UtilsService
  ) {
    super();
  }

  refreshApplication() {
    this._refreshApplication$.next();
  }

  /** Update the files of an application document.
   *  @param documentId The document to update
   *  @param metaFileLinkId The ID of the front side metaFileLink
   *  @param backSideMetaFileLinkId The ID of the back side metaFileLink (relevant only for ID documents)
   */
  updateDocument(documentId: number, metaFileLinkId: number, backSideMetaFileLinkId: number = null) {
    return this.applicationId$
      .pipe(take(1),
        switchMap(applicationId =>
          this.unitBankApplicationDataService.updateDocument(applicationId, documentId, metaFileLinkId, backSideMetaFileLinkId).pipe(
            catchError(error => {
              if (error instanceof BaseResponseDto) {
                this.utilsService.alertErrorMessage(error);
              } else {
                // to prevent the canceling of the entire observable.
                // didn't work.. error here still cause the observable to complete.
                var dialogParams = new AlertDialogParams();
                dialogParams.title = `An error has occurred`;
                dialogParams.description = `An error has occurred, please try again or contact support.`;
                dialogParams.actionLabel = `OK`;
                this.dialogService.alertDialog(dialogParams);
              }
              return of(null);
            }))
        ),
        catchError(error => {
          this.loggerService.error(`Error in BankApplicationService => updateDocument(). documentId: ${documentId} metaFileLinkId: ${metaFileLinkId}`, error);
          return throwError(error);
        })
      );
  }

  sendVerificationCode(): Observable<PhoneVerificationReqRes> {
    return this.applicationId$.pipe(
      take(1),
      switchMap(applicationId => this.unitBankApplicationDataService.sendVerificationCode(applicationId))
    );
  }

  resendVerificationCode(): Observable<PhoneVerificationReqRes> {
    return this.applicationId$.pipe(
      take(1),
      switchMap(applicationId => this.unitBankApplicationDataService.resendVerificationCode(applicationId))
    );
  }

  verifyPhone(request: PhoneVerificationReq): Observable<PhoneVerificationReqRes> {
    return this.applicationId$.pipe(
      take(1),
      switchMap(applicationId => this.unitBankApplicationDataService.verifyPhone(applicationId, request))
    );
  }

  deleteDraft() {
    return this.applicationId$.pipe(
      switchMap(applicationId => {
        if (Number.isNaN(applicationId))
          throw new Error("Application was not selected", {
            cause: { code: "NaNApplicationId" },
          });
        return this.bankApplicationDataService.deleteDraft(applicationId);
      })
    );
  }
}
